import { useState, useMemo } from "react";

export const useAnalysisLogic = stats => {
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
	const [selectedCategories, setSelectedCategories] = useState([
		"housing",
		"food",
		"general",
		"health",
		"personal",
	]);

	const allCategories = ["housing", "food", "general", "health", "personal"];

	// Memoized computed values
	const availableYears = useMemo(() => {
		if (!stats?.yearly) return [new Date().getFullYear()];
		return Object.keys(stats.yearly)
			.map(year => parseInt(year))
			.sort((a, b) => b - a);
	}, [stats?.yearly]);

	const handleCategoryToggle = category => {
		setSelectedCategories(prev =>
			prev.includes(category)
				? prev.filter(cat => cat !== category)
				: [...prev, category]
		);
	};

	// Prepare data for expense bar chart
	const expenseBarData = useMemo(() => {
		if (!stats?.monthly || !stats?.displayMetrics) return [];

		const yearlyMonthlyData = stats.monthly[selectedYear] || {};
		const monthsToShow = stats.displayMetrics[selectedYear]?.monthsToShow || [];

		if (monthsToShow.length === 0) return [];

		return monthsToShow.map(month => {
			const monthData = yearlyMonthlyData[month] || {};
			const monthName = `${selectedYear}-${String(month + 1).padStart(2, "0")}`;

			const categories = {};
			selectedCategories.forEach(category => {
				categories[category] = monthData[category] || 0;
			});

			return {
				month: monthName,
				categories,
			};
		});
	}, [stats?.monthly, stats?.displayMetrics, selectedYear, selectedCategories]);

	// Prepare data for expense pie chart
	const expensePieData = useMemo(() => {
		if (!stats?.yearly) return [];

		const yearData = stats.yearly[selectedYear] || {};
		const total = selectedCategories.reduce(
			(sum, cat) => sum + (yearData[cat] || 0),
			0
		);

		if (total === 0) return [];

		return selectedCategories
			.map(category => ({
				category,
				value: yearData[category] || 0,
				percentage: Math.round(((yearData[category] || 0) / total) * 100),
			}))
			.filter(item => item.value > 0)
			.sort((a, b) => b.value - a.value);
	}, [stats?.yearly, selectedYear, selectedCategories]);

	// Prepare data for overview bar chart (income, expenses, balance)
	const overviewBarData = useMemo(() => {
		if (!stats?.monthly || !stats?.displayMetrics) return [];

		const yearlyMonthlyData = stats.monthly[selectedYear] || {};
		const monthsToShow = stats.displayMetrics[selectedYear]?.monthsToShow || [];

		if (monthsToShow.length === 0) return [];

		return monthsToShow.map(month => {
			const monthData = yearlyMonthlyData[month] || {};
			const monthName = `${selectedYear}-${String(month + 1).padStart(2, "0")}`;

			return {
				month: monthName,
				income: monthData.totalIncome || 0,
				expenses: monthData.totalExpenses || 0,
				balance: monthData.balance || 0,
			};
		});
	}, [stats?.monthly, stats?.displayMetrics, selectedYear]);

	// Prepare data for income stacked bar chart
	const incomeStackedBarData = useMemo(() => {
		if (!stats?.monthly || !stats?.displayMetrics) return [];

		const yearlyMonthlyData = stats.monthly[selectedYear] || {};
		const monthsToShow = stats.displayMetrics[selectedYear]?.monthsToShow || [];

		if (monthsToShow.length === 0) return [];

		return monthsToShow.map(month => {
			const monthData = yearlyMonthlyData[month] || {};
			const monthName = `${selectedYear}-${String(month + 1).padStart(2, "0")}`;

			return {
				month: monthName,
				totalIncome: monthData.totalIncome || 0,
				incomeSubCategories: monthData.incomeSubCategories || {},
			};
		});
	}, [stats?.monthly, stats?.displayMetrics, selectedYear]);

	// Prepare data for income pie chart by subcategory
	const incomePieData = useMemo(() => {
		if (!stats?.yearly) return [];

		const yearData = stats.yearly[selectedYear] || {};
		const totalIncome = yearData.totalIncome || 0;

		if (totalIncome === 0) return [];

		const incomeSubCategories = yearData.incomeSubCategories || {};

		if (Object.keys(incomeSubCategories).length === 0) {
			return [
				{
					subCategory: "total",
					value: totalIncome,
					label: "Total des revenus",
					percentage: 100,
				},
			];
		}

		return Object.entries(incomeSubCategories)
			.map(([subCategory, value]) => ({
				subCategory,
				value,
				label: subCategory,
				percentage: Math.round((value / totalIncome) * 100),
			}))
			.filter(item => item.value > 0)
			.sort((a, b) => b.value - a.value);
	}, [stats?.yearly, selectedYear]);

	// Prepare data for yearly category trend line chart
	const yearlyCategoryTrendData = useMemo(() => {
		if (!stats?.yearly) return [];

		const currentYear = new Date().getFullYear();

		return Object.entries(stats.yearly)
			.map(([year, data]) => {
				const yearInt = parseInt(year);
				const isCurrentYear = yearInt === currentYear;

				const categories = selectedCategories.reduce((acc, category) => {
					acc[category] = data[category] || 0;
					return acc;
				}, {});

				// For current year, add projection data
				if (isCurrentYear && stats.averages && stats.averages[year]) {
					const yearAverages = stats.averages[year];
					const monthsForAverage = yearAverages.monthsForAverage || 1;

					const projectedCategories = selectedCategories.reduce(
						(acc, category) => {
							const monthlyAverage = yearAverages.categories?.[category] || 0;
							// Project to full year (12 months)
							acc[category] = monthlyAverage * 12;
							return acc;
						},
						{}
					);

					return {
						year: yearInt,
						categories,
						projectedCategories,
						isCurrentYear: true,
						monthsForAverage,
					};
				}

				return {
					year: yearInt,
					categories,
					projectedCategories: categories, // Same as actual for past years
					isCurrentYear: false,
				};
			})
			.filter(entry => Object.values(entry.categories).some(val => val > 0))
			.sort((a, b) => a.year - b.year); // Ascending by year
	}, [stats?.yearly, stats?.averages, selectedCategories]);

	// Prepare data for monthly balance area chart
	const monthlyBalanceData = useMemo(() => {
		if (!stats?.monthly || !stats?.displayMetrics) return [];

		const yearlyMonthlyData = stats.monthly[selectedYear] || {};
		const monthsToShow = stats.displayMetrics[selectedYear]?.monthsToShow || [];

		if (monthsToShow.length === 0) return [];

		return monthsToShow.map(month => {
			const monthData = yearlyMonthlyData[month] || {};
			const monthName = `${selectedYear}-${String(month + 1).padStart(2, "0")}`;

			return {
				month: monthName,
				monthIndex: month,
				totalIncome: monthData.totalIncome || 0,
				totalExpenses: monthData.totalExpenses || 0,
				balance: monthData.balance || 0,
			};
		});
	}, [stats?.monthly, stats?.displayMetrics, selectedYear]);

	// Prepare data for yearly balance area chart
	// Prepare data for yearly balance area chart
	const yearlyBalanceData = useMemo(() => {
		if (!stats?.yearly) return [];

		const currentYear = new Date().getFullYear();

		return Object.entries(stats.yearly)
			.map(([year, data]) => {
				const yearInt = parseInt(year);
				const isCurrentYear = yearInt === currentYear;

				const actualData = {
					year: yearInt,
					totalIncome: data.totalIncome || 0,
					totalExpenses: data.totalExpenses || 0,
					balance: (data.totalIncome || 0) - (data.totalExpenses || 0),
				};

				// For current year, add projection data
				if (isCurrentYear && stats.averages && stats.averages[year]) {
					const yearAverages = stats.averages[year];
					const projectedIncome = (yearAverages.income || 0) * 12;
					const projectedExpenses = (yearAverages.expenses || 0) * 12;
					const projectedBalance = projectedIncome - projectedExpenses;

					return {
						...actualData,
						projectedIncome,
						projectedExpenses,
						projectedBalance,
						isCurrentYear: true,
						monthsForAverage: yearAverages.monthsForAverage || 1,
					};
				}

				return {
					...actualData,
					projectedIncome: actualData.totalIncome,
					projectedExpenses: actualData.totalExpenses,
					projectedBalance: actualData.balance,
					isCurrentYear: false,
				};
			})
			.filter(entry => entry.totalIncome > 0 || entry.totalExpenses > 0)
			.sort((a, b) => a.year - b.year);
	}, [stats?.yearly, stats?.averages]);

	// Calculate expense averages using backend data
	const expenseAverages = useMemo(() => {
		if (!stats?.averages || !stats?.averages[selectedYear]) return {};

		const yearAverages = stats.averages[selectedYear].categories || {};
		const filteredAverages = {};

		selectedCategories.forEach(category => {
			filteredAverages[category] = yearAverages[category] || 0;
		});

		return filteredAverages;
	}, [stats?.averages, selectedYear, selectedCategories]);

	// Calculate averages using backend data
	const averagesData = useMemo(() => {
		if (!stats?.averages || !stats?.averages[selectedYear]) return {};

		const yearAverages = stats.averages[selectedYear];
		return {
			income: yearAverages.income || 0,
			expenses: yearAverages.expenses || 0,
			balance: yearAverages.balance || 0,
		};
	}, [stats?.averages, selectedYear]);

	// Check if there's any data for the selected year
	const hasDataForYear = useMemo(() => {
		if (!stats?.yearly || !stats?.monthly) return false;

		const yearData = stats.yearly[selectedYear] || {};
		const monthlyData = stats.monthly[selectedYear] || {};

		// Check if there's any expense data
		const hasExpenseData = allCategories.some(cat => (yearData[cat] || 0) > 0);
		// Check if there's any income data
		const hasIncomeData = (yearData.totalIncome || 0) > 0;
		// Check if there's any monthly data
		const hasMonthlyData = Object.keys(monthlyData).length > 0;

		return hasExpenseData || hasIncomeData || hasMonthlyData;
	}, [stats?.yearly, stats?.monthly, selectedYear, allCategories]);

	// Check if specific chart has data:
	// Check if montly expenses bar chart has data
	const hasExpenseBarData = useMemo(() => {
		return (
			expenseBarData.length > 0 &&
			expenseBarData.some(monthData =>
				Object.values(monthData.categories).some(value => value > 0)
			)
		);
	}, [expenseBarData]);

	// Check if monthly expenses pie chart has data
	const hasExpensePieData = useMemo(() => {
		return expensePieData.length > 0;
	}, [expensePieData]);

	// Check if overview bar chart has data
	const hasOverviewBarData = useMemo(() => {
		return (
			overviewBarData.length > 0 &&
			overviewBarData.some(
				monthData =>
					(monthData.income || 0) > 0 ||
					(monthData.expenses || 0) > 0 ||
					(monthData.balance || 0) !== 0
			)
		);
	}, [overviewBarData]);

	// Check if monthly income stacked bar chart has data
	const hasIncomeStackedBarData = useMemo(() => {
		return (
			incomeStackedBarData.length > 0 &&
			incomeStackedBarData.some(monthData => {
				const hasIncome = (monthData.totalIncome || 0) > 0;
				const hasSubCategories =
					Object.keys(monthData.incomeSubCategories || {}).length > 0;
				return hasIncome || hasSubCategories;
			})
		);
	}, [incomeStackedBarData]);

	// Check if income pie chart has data
	const hasIncomePieData = useMemo(() => {
		return incomePieData.length > 0;
	}, [incomePieData]);

	// Check if yearly category trend line chart has data
	const hasYearlyCategoryTrendData = useMemo(() => {
		return (
			yearlyCategoryTrendData.length > 0 &&
			yearlyCategoryTrendData.some(entry =>
				Object.values(entry.categories).some(value => value > 0)
			)
		);
	}, [yearlyCategoryTrendData]);

	// Check if monthly balance area chart has data
	const hasMonthlyBalanceData = useMemo(() => {
		return (
			monthlyBalanceData.length > 0 &&
			monthlyBalanceData.some(
				monthData =>
					(monthData.totalIncome || 0) > 0 ||
					(monthData.totalExpenses || 0) > 0 ||
					(monthData.balance || 0) !== 0
			)
		);
	}, [monthlyBalanceData]);

	// Check if yearly balance area chart has data
	const hasYearlyBalanceData = useMemo(() => {
		return yearlyBalanceData.length > 0;
	}, [yearlyBalanceData]);

	const hasAnyChartData = useMemo(() => {
		return (
			hasExpenseBarData ||
			hasExpensePieData ||
			hasYearlyBalanceData ||
			hasMonthlyBalanceData ||
			hasOverviewBarData ||
			hasIncomeStackedBarData ||
			hasIncomePieData
		);
	}, [
		hasExpenseBarData,
		hasExpensePieData,
		hasYearlyBalanceData,
		hasMonthlyBalanceData,
		hasOverviewBarData,
		hasIncomeStackedBarData,
		hasIncomePieData,
	]);

	return {
		// State
		selectedYear,
		setSelectedYear,
		selectedCategories,
		setSelectedCategories,
		allCategories,

		// Handlers
		handleCategoryToggle,

		// Computed data
		availableYears,
		expenseBarData,
		expenseAverages,
		monthlyBalanceData,
		yearlyBalanceData,
		overviewBarData,
		incomeStackedBarData,
		incomePieData,
		averagesData,
		expensePieData,
		yearlyCategoryTrendData,
		yearlyGrowthRates: stats?.yearlyGrowthRates || {},

		// Data availability
		hasDataForYear,
		hasExpenseBarData,
		hasExpensePieData,
		hasMonthlyBalanceData,
		hasYearlyBalanceData,
		hasOverviewBarData,
		hasIncomeStackedBarData,
		hasIncomePieData,
		hasAnyChartData,
		hasYearlyCategoryTrendData,
	};
};
